import React, { useState, useEffect } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { useParent } from "../../../contexts/ParentContext";
import { useAccount } from "../../../contexts/AccountContext";
import { storagePaths } from "@visitproof/types";
import { Switch, SwitchField } from "../../../design/switch";

interface Props {
  parentId: string;
}

const UploadFileButton: React.FC<Props> = ({ parentId }) => {
  const { addFileToParent } = useParent();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isVisibleToSupervisor, setIsVisibleToSupervisor] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (file && !name) {
      setName(file.name);
    }
  }, [file, name]);

  const handleSubmit = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);

    try {
      const path = account?.id
        ? storagePaths.parentFile(account.id, parentId, file.name)
        : `parents/${parentId}/${file.name}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      await addFileToParent(parentId, {
        path: fileRef.fullPath,
        name,
        description,
        isVisibleToSupervisor,
      });
      clearForm();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again later.");
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create File
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Upload File</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>File</Label>
                <Input
                  type="file"
                  onChange={(e) =>
                    setFile(e.target.files ? e.target.files[0] : null)
                  }
                />
              </Field>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </Field>

              <SwitchField>
                <Label>Visible to Monitor</Label>
                <Switch
                  checked={isVisibleToSupervisor}
                  onChange={() =>
                    setIsVisibleToSupervisor(!isVisibleToSupervisor)
                  }
                />
              </SwitchField>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={!file || isUploading} onClick={handleSubmit}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadFileButton;
